import { useState, useEffect, useRef, useCallback } from "react";

const DEFAULT_IDLE_MS = 450;
const DEFAULT_MOUNT_GRACE_MS = 900;
const SCROLL_DELTA_PX = 10;

export type MobileFabVisibilityOptions = {
  /** Ignore scroll-hide briefly after enable (layout shifts, scroll restoration). */
  mountGraceMs?: number;
  /** When false, the FAB stays visible (e.g. dashboard home primary CTA). */
  hideWhileScrolling?: boolean;
};

/**
 * When `enabled` (typically mobile), optionally hides floating actions while the
 * window scrolls and shows them again `idleDelayMs` after scrolling stops.
 */
export function useMobileFabVisibility(
  enabled: boolean,
  idleDelayMs: number = DEFAULT_IDLE_MS,
  options: MobileFabVisibilityOptions = {},
): boolean {
  const { mountGraceMs = DEFAULT_MOUNT_GRACE_MS, hideWhileScrolling = true } =
    options;

  const [visible, setVisible] = useState(true);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enabledAtRef = useRef(0);
  const lastScrollYRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const scheduleShow = useCallback(() => {
    clearTimer();
    idleTimerRef.current = setTimeout(() => {
      setVisible(true);
      idleTimerRef.current = null;
    }, idleDelayMs);
  }, [clearTimer, idleDelayMs]);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      setVisible(true);
      return;
    }

    if (!hideWhileScrolling) {
      clearTimer();
      setVisible(true);
      return;
    }

    enabledAtRef.current = Date.now();
    lastScrollYRef.current = window.scrollY;
    setVisible(true);

    const onScroll = () => {
      if (Date.now() - enabledAtRef.current < mountGraceMs) {
        return;
      }

      const scrollY = window.scrollY;
      if (Math.abs(scrollY - lastScrollYRef.current) < SCROLL_DELTA_PX) {
        return;
      }
      lastScrollYRef.current = scrollY;

      setVisible(false);
      scheduleShow();
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimer();
    };
  }, [
    enabled,
    hideWhileScrolling,
    mountGraceMs,
    scheduleShow,
    clearTimer,
  ]);

  return visible;
}
